import React, { useState } from 'react';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Group,
    Select,
    FileInput,
    Button,
    Progress,
    Center,
    Stack,
} from '@mantine/core';
import { IconCloudUpload, IconWorld, IconLanguage, IconCheck, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';

function AdminDataUploader() {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;

    const [file, setFile] = useState(null);
    const [region, setRegion] = useState('');
    const [language, setLanguage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleUpload = async () => {
        if (!file || !region || !language) return;

        setIsLoading(true);
        setUploadProgress(0);

        const storage = getStorage();
        const storageRef = ref(storage, `refresh_uploads/${Date.now()}-${file.name}`);
        const metadata = {
            customMetadata: {
                'region': region,
                'language': language
            }
        };

        const uploadTask = uploadBytesResumable(storageRef, file, metadata);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
            },
            (error) => {
                console.error("Upload failed:", error);
                setIsLoading(false);
                notifications.show({
                    title: 'Upload Failed',
                    message: `An error occurred: ${error.code}`,
                    color: 'red',
                    icon: <IconX size={18} />,
                });
            },
            () => {
                setIsLoading(false);
                notifications.show({
                    title: 'Upload Successful!',
                    message: 'The file has been received and will be processed in the background shortly.',
                    color: 'green',
                    icon: <IconCheck size={18} />,
                });
                setFile(null);
                setRegion('');
                setLanguage('');
                setUploadProgress(0);
            }
        );
    };

    return (
        <Box
            style={{
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
                backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                overflow: 'auto',
            }}
        >
            <Container fluid px="xl" py="xl" style={{ flexGrow: 1 }}>
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconCloudUpload
                        color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]}
                        style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }}
                    />
                    Refresh Radar Uploader
                </Title>
                <Text size="xl" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    Upload the latest Power BI report to update the Refresh Radar data.
                </Text>

                <Center>
                    <Paper
                        withBorder
                        shadow="md"
                        p={30}
                        radius="md"
                        style={{
                            maxWidth: '600px',
                            width: '100%',
                            backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                        }}
                    >
                        <Stack gap="md">
                            <Text size="sm" c="dimmed">
                                Export the latest data from the Power BI report as an .xlsx file. Select the correct region and language for the data slice before uploading.
                            </Text>

                            <Group grow>
                                <Select
                                    label="Region"
                                    placeholder="Select region"
                                    leftSection={<IconWorld size={16} />}
                                    data={['UK', 'US', '--','AE', 'AR', 'AT', 'AU', 'BE', 'CA', 'CH', 'CL', 'CO', 'DE', 'ES', 'FR', 'IE', 'IN', 'MX', 'MY', 'NZ', 'PE', 'PH', 'SG', 'XL', 'ZA']}
                                    value={region}
                                    onChange={(value) => setRegion(value || '')}
                                    disabled={isLoading}
                                    required
                                />
                                <Select
                                    label="Language"
                                    placeholder="Select language"
                                    leftSection={<IconLanguage size={16} />}
                                    data={['EN', 'DE', 'ES', 'FR']}
                                    value={language}
                                    onChange={(value) => setLanguage(value || '')}
                                    disabled={isLoading}
                                    required
                                />
                            </Group>

                            <FileInput
                                label="Refresh Data File (.xlsx)"
                                placeholder="Click to select file"
                                value={file}
                                onChange={setFile}
                                leftSection={<IconCloudUpload size={16} />}
                                accept=".xlsx"
                                disabled={isLoading}
                                required
                                clearable
                            />

                            {isLoading && (
                                <Progress value={uploadProgress} animated size="lg" radius="sm" />
                            )}

                            <Button
                                fullWidth
                                mt="md"
                                onClick={handleUpload}
                                loading={isLoading}
                                disabled={!file || !region || !language || isLoading}
                                size="md"
                                leftSection={<IconCloudUpload size={18} />}
                            >
                                Upload and Process File
                            </Button>
                        </Stack>
                    </Paper>
                </Center>
            </Container>
        </Box>
    );
}

export default AdminDataUploader;